import React, { useMemo } from "react";

import {
  Stack,
  TextField,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SearchIcon from "@mui/icons-material/Search";

import {
  useListMeeting,
  useListUsers,
  useListOrganisations,
  useListClients,
  useListPatients,
} from "../../../store";
import { getMeetingStatus } from "../../../types-helpers";
import {
  CalendarSlot,
  MeetingStatus,
  Organisation,
  PatientSummary,
  User,
} from "../../../types";
import {
  createTeamDictionary,
  createPatientDictionary,
  createUserDictionary,
} from "../../../utils/create-dictionary";
import AdminMeetingsTable from "./admin-meetings-table";
import MeetingsStatusFilter from "./admin-meetings-status-filter";

type AdminMeetingsTabProps = {
  selectedClientId?: string;
};

const AdminMeetingsTab: React.FC<AdminMeetingsTabProps> = ({
  selectedClientId,
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [cardFilter, setCardFilter] = React.useState<MeetingStatus | null>(
    null,
  );
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [sortBy, setSortBy] = React.useState<string>("startTime");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [page, setPage] = React.useState(0);

  // Only load meetings when a client is selected
  const { data: meetings = [], isLoading: meetingsLoading } = useListMeeting(
    startDate || undefined,
    endDate || undefined,
    selectedClientId,
  );

  const { data: clients = [], isLoading: clientsLoading } = useListClients();
  const { data: users = [], isLoading: usersLoading } =
    useListUsers(true, selectedClientId);
  const { data: teams = [], isLoading: teamsLoading } =
    useListOrganisations(selectedClientId);
  const { data: patients = [], isLoading: patientsLoading } = useListPatients();

  const teamDictionary = useMemo<Record<string, Organisation>>(() => {
    return createTeamDictionary(teams);
  }, [teams]);

  const userDictionary = useMemo<Record<string, User>>(() => {
    return createUserDictionary(users);
  }, [users]);

  const patientDictionary = useMemo<Record<string, PatientSummary>>(() => {
    return createPatientDictionary(patients);
  }, [patients]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const sortMeetings = (meetings: CalendarSlot[]) => {
    return [...meetings].sort((a, b) => {
      let aValue: any = a[sortBy as keyof CalendarSlot];
      let bValue: any = b[sortBy as keyof CalendarSlot];

      // Handle special cases for sorting
      if (sortBy === "startTime" || sortBy === "endTime") {
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (sortBy === "creatorId") {
        aValue = getUserName(aValue).toLowerCase();
        bValue = getUserName(bValue).toLowerCase();
      } else if (sortBy === "organisationId") {
        aValue = getTeamName(aValue).toLowerCase();
        bValue = getTeamName(bValue).toLowerCase();
        //   } else if (sortBy === "client") { // TODO implement client sorting
        //     aValue = getClientName(a.organisationId).toLowerCase();
        //     bValue = getClientName(b.organisationId).toLowerCase();
      } else if (sortBy === "duration") {
        aValue = getDurationInMinutes(a.startTime, a.endTime);
        bValue = getDurationInMinutes(b.startTime, b.endTime);
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || "";
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Calculate statistics using meetings for the selected client
  const totalMeetings = meetings.length;

  const meetingDictionary = useMemo(() => {
    const meetingDictionary: Record<MeetingStatus, CalendarSlot[]> = {
      [MeetingStatus.Started]: [],
      [MeetingStatus.Ended]: [],
      [MeetingStatus.Scheduled]: [],
      [MeetingStatus.Cancelled]: [],
      [MeetingStatus.Abandoned]: [],
      [MeetingStatus.Proposed]: [],
      [MeetingStatus.Training]: [],
    };

    for (let meeting of meetings) {
      const { status } = getMeetingStatus(meeting);

      meetingDictionary[status].push(meeting);
    }

    return meetingDictionary;
  }, [meetings]);

  let cardFilteredMeetings: CalendarSlot[] = cardFilter
    ? meetingDictionary[cardFilter]
    : meetings;

  const getUserName = (userId: string) => {
    const user = users.find((u) => u.userId === userId);
    return user?.fullName || "Unknown User";
  };

  const getTeamName = (teamId: string) => {
    const team = teams.find((team) => team.organisationId === teamId);
    return team?.name || "Unknown Team";
  };

  const getDurationInMinutes = (startTime: Date, endTime?: Date): number => {
    if (!endTime) return 0;
    const start = startTime instanceof Date ? startTime : new Date(startTime);
    const end = endTime instanceof Date ? endTime : new Date(endTime);
    const diffMs = end.getTime() - start.getTime();
    return Math.floor(diffMs / (1000 * 60));
  };

  // Filter meetings based on search term (client filtering already handled by useMeetingList)
  // Card Filtered meetings are applied before other filters
  let filteredMeetings = cardFilteredMeetings.filter((meeting) => {
    // Apply search term filter
    if (!searchTerm) return true;

    const searchTermLower = searchTerm.toLowerCase();

    // Check if any attendee matches the search term
    const attendeeMatch =
      meeting.attendeeIds?.some((attendeeId) =>
        getUserName(attendeeId).toLowerCase().includes(searchTermLower),
      ) || false;

    // Check if any team matches the search term
    const teamMatch =
      meeting.organisationIds?.some((teamId) =>
        getTeamName(teamId).toLowerCase().includes(searchTermLower),
      ) || false;

    return (
      meeting.patientId?.toLowerCase().includes(searchTermLower) ||
      getUserName(meeting.creatorId).toLowerCase().includes(searchTermLower) ||
      teamMatch ||
      attendeeMatch
    );
  });

  // Apply date filters
  if (startDate) {
    filteredMeetings = filteredMeetings.filter((meeting) => {
      const meetingDate = new Date(meeting.startTime);
      return meetingDate >= startDate;
    });
  }

  if (endDate) {
    filteredMeetings = filteredMeetings.filter((meeting) => {
      const meetingDate = new Date(meeting.startTime);
      return meetingDate <= endDate;
    });
  }

  // Apply sorting
  filteredMeetings = sortMeetings(filteredMeetings);

  const rowsPerPage = 25;
  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(filteredMeetings.length / rowsPerPage) - 1),
  );
  React.useEffect(() => {
    setPage(0);
  }, [searchTerm, cardFilter, startDate, endDate, sortBy, sortOrder]);

  return (
    <>
      <Stack spacing={0}>
        {/* Filters */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexGrow: 1, p: 1 }}
        >
          <TextField
            placeholder="Search meetings by patient, organiser, team, or attendee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(date) => setStartDate(date)}
            format="dd/MM/yyyy"
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(date) => setEndDate(date)}
            format="dd/MM/yyyy"
            slotProps={{ textField: { size: "small" } }}
          />
        </Stack>

        {/* Tab-style filters */}
        <MeetingsStatusFilter
          cardFilter={cardFilter}
          setCardFilter={setCardFilter}
          totalMeetings={totalMeetings}
          meetingDictionary={meetingDictionary}
        />

        <AdminMeetingsTable
          meetingsLoading={meetingsLoading}
          usersLoading={usersLoading}
          teamsLoading={teamsLoading}
          clientsLoading={clientsLoading}
          patientsLoading={patientsLoading}
          filteredMeetings={filteredMeetings}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={(nextPage) => setPage(nextPage)}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          searchTerm={searchTerm}
          cardFilter={cardFilter}
          startDate={startDate}
          endDate={endDate}
          userDictionary={userDictionary}
          teamDictionary={teamDictionary}
          patientDictionary={patientDictionary}
          selectedClientId={selectedClientId || ""}
        />
      </Stack>
    </>
  );
};

export default AdminMeetingsTab;
